import React from "react";
import { Link, graphql } from "gatsby";
import { Div, Image, Text } from "atomize";

import Layout from "../components/common/layout";
import Header from "../components/common/Header";

import Footer from "../components/common/Footer";
import SEO from "../components/seo";

import WebsiteContainer from "../components/common/WebsiteContainer";
import CustomContainer from "../components/common/CustomContainer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return (
        <Text textSize={{ xs: "subheader5", md: "subheader5" }} m="1rem 0">
          {children}
        </Text>
      );
    },
    [BLOCKS.HEADING_1]: (node, children) => (
      <Text
        tag="h1"
        m={{ t: "1rem", b: "3rem" }}
        textSize={{ xs: "subheader3", md: "display3" }}
        textWeight="700"
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Text
        textSize={{ xs: "heading", md: "display5" }}
        textWeight="700"
        m={{ t: "1rem", b: "2rem" }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Text
        textSize={{ xs: "subheader2", md: "title3" }}
        textWeight="700"
        m={{ t: "1rem", b: "1rem" }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Text
        textSize={{ xs: "subheader1", md: "paragraph3" }}
        textWeight="700"
        m={{ t: "1rem", b: "1rem" }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Text
        textSize={{ xs: "subheader1", md: "paragraph3" }}
        textWeight="700"
        m={{ t: "1rem", b: "1rem" }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Div
        textSize={{ xs: "subheader1", md: "subheader2" }}
        textWeight="700"
        m={{ t: "1rem", b: "1rem" }}
        dangerouslySetInnerHTML={{ __html: children }}
      />
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol>
        {node.content.map(item => {
          return documentToReactComponents(item, options);
        })}
      </ol>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul>
        {node.content.map(item => {
          return documentToReactComponents(item, options);
        })}
      </ul>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => <Text tag="li">{children}</Text>,
    [BLOCKS.HR]: (node, children) => (
      <Div h="1px" bg="border">
        {children}
      </Div>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <Text
        textSize={{ xs: "subheader1", md: "subheader3" }}
        m="1rem 0"
        textAlign="center"
        textWeight="700"
      >
        {children}
      </Text>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      if ("embed" in node.data.target.fields) {
        return (
          <Div
            dangerouslySetInnerHTML={{
              __html: node.data.target.fields.embed["en-US"],
            }}
          ></Div>
        );
      }

      return <Text>{children}</Text>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
      const type = node.data.target.fields.file["en-US"].contentType;

      switch (type) {
        case "image/gif":
        case "image/png":
        case "image/jpg":
        case "image/jpeg":
          return (
            <Image
              alt={
                node.data.target.fields.description &&
                node.data.target.fields.description["en-US"]
              }
              src={node.data.target.fields.file["en-US"].url}
            />
          );
      }
      return false;
    },
  },
};

const Terms = ({ data }) => {
  const blog = data.contentfulBlog;

  return (
    <Layout>
      <Header headerFixed={true} data={{ contentfulJson: data.headerJson }} />
      <SEO
        seo={data.contentfulBlog.seo}
        url={`https://trend.io/${blog.slug}`}
      />
      <WebsiteContainer>
        <CustomContainer>
          <Div
            p={{
              t: { xs: "0", md: "3.6rem" },
            }}
            m={{ xs: "0 -1.5rem", md: "0" }}
          >
            <Div
              p={{
                t: { xs: "2rem", md: "4.8rem" },
                b: { xs: "4rem", md: "4.5rem" },
              }}
              bg="white"
              textColor="black"
            >
              <Div maxW="48.5rem" m="0 auto">
                {documentToReactComponents(blog.content.json, options)}
              </Div>
            </Div>
          </Div>
        </CustomContainer>
      </WebsiteContainer>
      <Footer
        contentfulFooter={data.contentfulFooter}
        contentfulGlobalConfig={data.contentfulGlobalConfig}
      />
    </Layout>
  );
};

export default Terms;

export const query = graphql`
  query TermsQuery {
    contentfulFooter(contentful_id: { eq: "5noUnCCOBDl8Is2Ppw2Nty" }) {
      solutions {
        label
        link
        isExternal
      }
      menu {
        label
        link
        isExternal
      }
      links {
        label
        link
        isExternal
      }
      instagramLink
      logoLink
    }

    contentfulGlobalConfig {
      footerForm {
        footerForm
      }
    }

    headerJson: contentfulJson(
      contentful_id: { eq: "2NCeKxLHd9JagUBAEsXDMo" }
    ) {
      json {
        json
      }
    }

    contentfulBlog(slug: { eq: "terms" }) {
      id
      title
      content {
        json
      }
      slug
      seo {
        ...SeoFragment
      }
    }
  }
`;
